import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Section from '../Section';
import Breadcrumb from '../Breadcrumb';
import BannerSectionStyle9 from '../Section/BannerSection/BannerSectionStyle9';
import { Icon } from '@iconify/react';
import Spacing from '../Spacing';
import Post from '../Post';
import Sidebar from '../Sidebar';
import AuthorWidget from '../Widget/AuthorWidget';
import CommentsWidget from '../Widget/CommentsWidget';
import ReplyWidget from '../Widget/ReplyWidget';
import { pageTitle } from '../../helpers/PageTitle';

// Blog data with unique content for each blog
const blogsData = {
    'health-checkups-importance': {
        title: 'Blog 1: Why Regular Health Checkups Are Important for Every Family',
        category: 'Health Awareness • Preventive Care • Diagnostics',
        date: 'January 15, 2024',
        author: 'BRR Diagnostics | Proddatur',
        bannerImage: '/images/blog/health-checkups.png',
        tags: [
            { tag: 'Health Awareness', href: '/blog' },
            { tag: 'Preventive Care', href: '/blog' },
            { tag: 'Diagnostics', href: '/blog' },
        ],
        content: {
            intro: `Many serious health problems do not show symptoms in their early stages. Conditions like diabetes, high cholesterol, thyroid imbalance, and kidney issues often develop silently. Regular health checkups help detect these problems before they become serious.
            
Preventive testing is not only for older people. Today’s lifestyle, stress, and food habits make routine testing important for all age groups.`,
            sections: [
                {
                    heading: 'How Diagnostic Tests Protect Your Health',
                    text: `At BRR Diagnostics, health checkups include blood tests, urine analysis, hormone tests, and imaging when required. These tests give a clear picture of how the body is functioning internally.
                    
Common tests in routine checkups include:
• Blood sugar and cholesterol tests
• Liver and kidney function tests
• Thyroid and hormone tests
• Complete Blood Count (CBC)

Early detection allows doctors to suggest timely treatment and lifestyle changes.`,
                },
                {
                    heading: 'Why Choose BRR Diagnostics',
                    text: `With modern equipment and experienced professionals, BRR Diagnostics ensures accurate reports and patient-friendly service. Regular testing gives confidence, clarity, and peace of mind.
                    
BRR Diagnostics – Because prevention is better than cure.`,
                },
            ],
            comments: [
                { text: "Very helpful for understanding why routine tests matter.", author: "Rahul K.", date: "Jan 16, 2024" },
                { text: "Clear explanation for families.", author: "Priya M.", date: "Jan 18, 2024" }
            ],
            conclusion: `Regular health checkups are the best investment for a long, healthy, and happy life for you and your family.`,
            quote: '',
        },
    },
    'understanding-blood-tests': {
        title: 'Blog 2: Understanding Blood Tests and What They Reveal About Your Health',
        category: 'Blood Tests • Diagnostics • Health Education',
        date: 'January 22, 2024',
        author: 'BRR Diagnostics | Proddatur',
        bannerImage: '/images/blog/blood-tests.png',
        tags: [
            { tag: 'Blood Tests', href: '/blog' },
            { tag: 'Diagnostics', href: '/blog' },
            { tag: 'Health Education', href: '/blog' },
        ],
        content: {
            intro: `Blood tests are one of the most powerful tools in medical diagnosis. They help doctors understand infections, anemia, sugar levels, organ health, and hormonal balance.

Many symptoms like tiredness, weight changes, fever, or weakness can be explained through simple blood tests.`,
            sections: [
                {
                    heading: 'Key Blood Tests Explained',
                    text: `At BRR Diagnostics, blood testing is done using modern analyzers to ensure precision and reliability. Common tests include:
• CBC to check hemoglobin, platelets, and infections
• Blood sugar tests to detect diabetes
• Liver and kidney function tests
• Hormone and thyroid tests

These tests help doctors decide the correct treatment without delay.`,
                },
                {
                    heading: 'Accurate Testing Makes a Difference',
                    text: `Proper sample handling and timely reporting are essential. At BRR Diagnostics, every sample is treated with care because each report guides a medical decision.
                    
BRR Diagnostics – Accurate results you can trust.`,
                },
            ],
            comments: [
                { text: "Very easy to understand.", author: "Anil Kumar", date: "Jan 23, 2024" },
                { text: "Good explanation of blood tests.", author: "Sunita R.", date: "Jan 25, 2024" }
            ],
            conclusion: `Understanding your blood test reports is the first step towards taking control of your health and well-being.`,
            quote: '',
        },
    },
    'ultrasound-xray-diagnosis': {
        title: 'Blog 3: How Ultrasound and Digital X-Ray Help in Early Diagnosis',
        category: 'Radiology • Imaging • Diagnostics',
        date: 'February 1, 2024',
        author: 'BRR Diagnostics | Proddatur',
        bannerImage: '/images/blog/blog ultra sound.png',
        tags: [
            { tag: 'Radiology', href: '/blog' },
            { tag: 'Imaging', href: '/blog' },
            { tag: 'Diagnostics', href: '/blog' },
        ],
        content: {
            intro: `Ultrasound and Digital X-Ray play an important role in identifying internal problems that cannot be detected through blood tests alone. These scans help doctors visualize organs, bones, and tissues clearly.

Imaging is commonly used for abdominal issues, pregnancy monitoring, injuries, chest problems, and joint pain.`,
            sections: [
                {
                    heading: 'Role of Imaging in Modern Healthcare',
                    text: `Ultrasound and Digital X-Ray play an important role in identifying internal problems that cannot be detected through blood tests alone. These scans help doctors visualize organs, bones, and tissues clearly.
                    
Imaging is commonly used for abdominal issues, pregnancy monitoring, injuries, chest problems, and joint pain.`,
                },
                {
                    heading: 'Advanced Imaging at BRR Diagnostics',
                    text: `BRR Diagnostics offers Digital X-Ray and Ultrasound Scan services using modern equipment that provide clear images with reduced radiation exposure.
                    
Benefits include:
• Fast and accurate imaging
• Safe and non-invasive procedures
• Quick reporting for timely treatment`,
                },
                {
                    heading: 'Why Early Imaging Matters',
                    text: `Early imaging helps prevent complications and supports accurate diagnosis. With expert handling and patient-friendly care, BRR Diagnostics ensures comfort and clarity during every scan.
                    
BRR Diagnostics – Clear images. Confident decisions.`,
                },
            ],
            comments: [
                { text: "Helpful information about scans.", author: "Vikram S.", date: "Feb 2, 2024" },
                { text: "Explained in a simple way.", author: "Meena D.", date: "Feb 3, 2024" }
            ],
            conclusion: `Early diagnosis through accurate imaging ensures timely treatment and better health outcomes.`,
            quote: '',
        },
    },
};

// Related blogs for each page
const getRelatedBlogs = (currentSlug) => {
    const allBlogs = [
        {
            title: 'Why Regular Health Checkups Are Important for Every Family',
            thumbUrl: '/images/real/blog_1.png',
            date: 'January 15',
            btnText: 'Learn More',
            href: '/blog/health-checkups-importance',
        },
        {
            title: 'Understanding Blood Tests and What They Reveal About Your Health',
            thumbUrl: '/images/real/blog_2.png',
            date: 'January 22',
            btnText: 'Learn More',
            href: '/blog/understanding-blood-tests',
        },
        {
            title: 'How Ultrasound and Digital X-Ray Help in Early Diagnosis',
            thumbUrl: '/images/real/blog_3.png',
            date: 'February 1',
            btnText: 'Learn More',
            href: '/blog/ultrasound-xray-diagnosis',
        },
    ];
    return allBlogs.filter(blog => !blog.href.includes(currentSlug));
};

export default function BlogDetailPage() {
    const { blogId } = useParams();
    const blog = blogsData[blogId];

    if (!blog) {
        pageTitle('Blog Not Found');
        return (
            <>
                <Section topMd={170} bottomMd={54} bottomLg={54}>
                    <Breadcrumb title="Blog Not Found" />
                </Section>
                <div className="container">
                    <h2>Blog Not Found</h2>
                    <p>The requested blog could not be found.</p>
                    <Link to="/blog" className="cs_btn cs_style_1">
                        <span>Back to Blogs</span>
                    </Link>
                </div>
                <Spacing md="200" />
            </>
        );
    }

    pageTitle(blog.title);
    const relatedBlogs = getRelatedBlogs(blogId);

    return (
        <>
            <Section topMd={170} bottomMd={54} bottomLg={54}>
                <Breadcrumb title={blog.title} />
            </Section>
            <div className="container">
                <div className="cs_blog_details_info">
                    <div className="cs_blog_details_info_left">
                        <div className="cs_blog_details_tags">
                            {blog.tags.map((item, index) => (
                                <Link key={index} to={item.href}>
                                    {item.tag}
                                </Link>
                            ))}
                        </div>
                        <div className="cs_blog_details_date">
                            {blog.date} | {blog.author}
                        </div>
                    </div>
                </div>
                <Spacing md="55" />
                <img
                    src={blog.bannerImage}
                    alt={blog.title}
                    className="w-100 cs_radius_20"
                />
                <Spacing md="90" lg="50" />
                <div className="row">
                    <div className="col-lg-12">
                        <div className="cs_blog_details">
                            <p>{blog.content.intro}</p>

                            {blog.content.sections.map((section, index) => (
                                <div key={index}>
                                    <h2>{section.heading}</h2>
                                    <p style={{ whiteSpace: 'pre-line' }}>{section.text}</p>
                                </div>
                            ))}

                            {/* <blockquote
                                style={{
                                    backgroundImage: 'url("/images/blog/blog_details_2.jpeg")',
                                }}
                            >
                                <p>{blog.content.quote}</p>
                            </blockquote> */}

                            <h2>Conclusion</h2>
                            <p>{blog.content.conclusion}</p>
                        </div>
                        <Spacing md="85" />
                        <Spacing md="85" />
                        <AuthorWidget
                            imgUrl="/images/blog/author.png"
                            name={blog.author}
                            description={`${blog.author} is a medical professional at BRR Diagnostics, Proddatur. With years of experience in diagnostic healthcare, they are committed to providing accurate information to help patients understand their health better.`}
                        />
                        <Spacing md="110" />
                        {blog.content.comments && (
                            <CommentsWidget title="Comments" comments={blog.content.comments} />
                        )}
                        <Spacing md="92" />
                        <ReplyWidget title="Leave a Reply" />
                    </div>
                    {/* <div className="col-lg-4">
                        <Sidebar />
                    </div> */}
                </div>
                <Spacing md="135" lg="100" />
                <h2 className="mb-0 cs_fs_40 cs_medium">Related Articles</h2>
                <Spacing md="57" />
                <div className="row cs_gap_y_40">
                    {relatedBlogs?.map((item, index) => (
                        <div className="col-xl-4 col-md-6" key={index}>
                            <Post {...item} />
                        </div>
                    ))}
                </div>
            </div>
            <Spacing md="200" xl="150" lg="110" />
            {/* <Section className="cs_footer_margin_0">
                <BannerSectionStyle9
                    title="Don't Let Your Health <br />Take a Backseat!"
                    subTitle="Schedule an appointment with one of our experienced <br />medical professionals today!"
                    imgUrl="/images/doctors/banner_img_3.png"
                />
            </Section> */}
        </>
    );
}
